import { createTheme } from '@mui/material/styles';

/**
 * Explore workbench theme — "deep slate".
 *
 * Scoped to the Explore page only (applied via a nested ThemeProvider in
 * Explore.tsx). The rest of the app keeps the default theme in src/theme.ts.
 *
 * Design intent: a refined, modern legal-tech surface (Legora / Harvey
 * adjacent), not a Material-Design admin tool.
 *   - Ink-on-near-white. No Material blue. The accent is a deep slate,
 *     used sparingly — primary actions and selection, nothing else.
 *   - Separation by whitespace + hairline dividers, not boxed cards.
 *   - Typography-forward: Inter, tightened scale, no shouty ALL-CAPS.
 *   - Soft, quiet: 8px radii, no drop shadows on outlined surfaces.
 */

// ---- Palette tokens -------------------------------------------------------

const INK = '#1a1d21';          // near-black primary text
const INK_SOFT = '#5b6169';     // secondary text
const SLATE = '#3a4452';        // the accent — deep slate
const SLATE_HOVER = '#2c343f';  // accent, pressed/hover
const SURFACE = '#ffffff';
const CANVAS = '#f7f8f9';       // app background — barely-grey
const HAIRLINE = 'rgba(26, 29, 33, 0.08)';  // dividers / outlined borders

const exploreTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: SLATE,
      dark: SLATE_HOVER,
      contrastText: '#ffffff',
    },
    secondary: {
      main: INK_SOFT,
    },
    text: {
      primary: INK,
      secondary: INK_SOFT,
    },
    background: {
      default: CANVAS,
      paper: SURFACE,
    },
    divider: HAIRLINE,
    success: { main: '#3f7d52' },   // muted, not the default neon green
    warning: { main: '#b7791f' },   // ochre, not Material orange
    error:   { main: '#b4453a' },   // brick, not Material red
    info:    { main: SLATE },
  },

  shape: {
    borderRadius: 8,
  },

  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    h4: { fontWeight: 600, letterSpacing: '-0.02em', fontSize: '1.6rem' },
    h5: { fontWeight: 600, letterSpacing: '-0.015em', fontSize: '1.3rem' },
    h6: { fontWeight: 600, letterSpacing: '-0.01em', fontSize: '1.05rem' },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    body1: { fontSize: '0.9rem', lineHeight: 1.55 },
    body2: { fontSize: '0.84rem', lineHeight: 1.5 },
    button: { fontWeight: 500, letterSpacing: 0 },
    overline: {
      fontWeight: 600,
      fontSize: '0.68rem',
      letterSpacing: '0.06em',
      color: INK_SOFT,
    },
    caption: { fontSize: '0.74rem', lineHeight: 1.45 },
  },

  components: {
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: { borderColor: HAIRLINE },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { textTransform: 'none', borderRadius: 7 },
        containedPrimary: {
          backgroundColor: SLATE,
          '&:hover': { backgroundColor: SLATE_HOVER },
        },
        outlined: {
          borderColor: HAIRLINE,
          color: INK,
          '&:hover': {
            borderColor: 'rgba(26, 29, 33, 0.2)',
            backgroundColor: 'rgba(26, 29, 33, 0.02)',
          },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: 6,
          // Default (uncolored) filled chip — quiet grey, not loud.
          '&.MuiChip-filledDefault': {
            backgroundColor: 'rgba(26, 29, 33, 0.06)',
            color: INK,
          },
        },
        outlined: { borderColor: HAIRLINE },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          minHeight: 44,
          color: INK_SOFT,
          '&.Mui-selected': { color: INK },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { height: 2, backgroundColor: INK },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': { borderColor: HAIRLINE },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(26, 29, 33, 0.18)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: SLATE,
            borderWidth: 1,
          },
        },
      },
    },

    MuiDivider: {
      styleOverrides: { root: { borderColor: HAIRLINE } },
    },

    MuiTableCell: {
      styleOverrides: {
        root: { borderBottomColor: HAIRLINE },
        head: {
          fontWeight: 600,
          color: INK_SOFT,
          fontSize: '0.74rem',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: INK,
          fontSize: '0.72rem',
          fontWeight: 400,
          borderRadius: 6,
        },
      },
    },
  },
});

export default exploreTheme;
